/**
 * SportsWalkInPage — Staff cashier booking on behalf of customers.
 * Desktop: POS-style layout — court/date/slots on left, sticky order ticket on right.
 * Mobile: stacked single column.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, UserPlus, Trophy, CheckCircle, X, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { sportsApi } from '../SportsAuthContext';
import SportsNavBar from '../components/SportsNavBar';
import axios from 'axios';
import { fmt12, ymdTz, addDaysYmd } from '../utils/sportsUtils';
import { QRCodeSVG } from 'qrcode.react';
import PaymentLinkModal from '../components/PaymentLinkModal';
import BookingDetailBody, { bookingBalance, bookingIsPaid } from '../components/BookingDetailBody';
import ProofPreviewButton from '../components/ProofPreview';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_OPTS = [
  { id: 'cash',     label: 'Cash',     icon: '💵', desc: 'Paid in person' },
  { id: 'gcash',    label: 'GCash',    icon: '📱', desc: 'GCash received' },
  { id: 'paymongo', label: 'PayMongo', icon: '🔗', desc: 'Send link/QR' },
  { id: 'unpaid',   label: 'Unpaid',   icon: '📋', desc: 'Floating booking' },
];

function pad(n) { return String(n).padStart(2, '0'); }
function toMin(t) { if (!t) return 0; const [h, m] = t.split(':').map(Number); return h * 60 + m; }

const DOW_FULL  = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const DOW_SHORT = ['SUN','MON','TUE','WED','THU','FRI','SAT'];
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtDateFull(ds) {
  const d = new Date(ds + 'T00:00:00');
  return `${DOW_FULL[d.getDay()]}, ${MONTH_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}
function fmtDateShort(ds) {
  const d = new Date(ds + 'T00:00:00');
  return `${DOW_SHORT[d.getDay()]} ${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
}

function useWindowWidth() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handle = () => setW(window.innerWidth);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return w;
}

/* ── Booking detail modal — the "customer file": full transaction + collect ──
   Payment always goes through amount → (change) → double-confirm. No one-tap paid. */
function WalkinBookingModal({ booking, onClose, onPaid }) {
  const b = booking;
  const balance = b.balance_due != null
    ? Math.max(0, parseFloat(b.balance_due) || 0)
    : Math.max(0, (parseFloat(b.amount) || 0) - (parseFloat(b.amount_paid) || 0));
  const isPaid = b.payment_status === 'paid' || balance <= 0;
  const collectable = !isPaid && ['confirmed', 'awaiting_confirmation'].includes(b.status);

  const [amt, setAmt] = useState(String(balance || ''));
  const [method, setMethod] = useState('cash');
  const [tendered, setTendered] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const amount = Math.min(parseFloat(amt) || 0, balance);
  const tenderedNum = parseFloat(tendered);
  const hasTendered = method === 'cash' && tendered !== '' && !Number.isNaN(tenderedNum);
  const change = hasTendered ? Math.max(0, tenderedNum - amount) : null;
  const shortCash = hasTendered && tenderedNum < amount;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await sportsApi.post(`/sports/bookings/${b.id}/receive-payment`,
        { amount, method, tendered: hasTendered ? tenderedNum : undefined });
      toast.success(`₱${amount.toLocaleString()} recorded (${method})`
        + (r.data.change > 0 ? ` — give ₱${Number(r.data.change).toLocaleString()} change` : ''));
      onPaid({ payment_status: r.data.payment_status, amount_paid: r.data.amount_paid,
               balance_due: r.data.balance_due, payment_method: method });
      setConfirming(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((d && (d.message || d)) || 'Could not record the payment');
    } finally { setBusy(false); }
  };

  const inp = { width: '100%', background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8, padding: '9px 12px', fontSize: 15, fontWeight: 700, outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#141414', border: '1px solid #2A2A2A', borderRadius: 16, padding: 22, width: '100%', maxWidth: 460, maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, fontWeight: 900, textTransform: 'uppercase', color: '#fff' }}>{b.customer?.name || '—'}</p>
            {b.ref_number && <p style={{ fontSize: 12, color: '#E63946', fontWeight: 800, letterSpacing: 1, fontFamily: 'Oswald, sans-serif', marginTop: 2 }}>{b.ref_number}</p>}
          </div>
          <button onClick={onClose} data-testid="walkin-detail-close"
            style={{ background: '#1E1E1E', border: 'none', color: '#737373', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 13, fontWeight: 800 }}>✕</button>
        </div>

        <BookingDetailBody booking={b} />

        {collectable && !confirming && (
          <div style={{ borderTop: '1px solid #1E1E1E', paddingTop: 12 }}>
            <p style={{ fontSize: 10, color: '#00E676', textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 800, marginBottom: 8 }}>Collect payment</p>
            <p style={{ fontSize: 10, color: '#555', textTransform: 'uppercase', marginBottom: 4 }}>Amount received (partial allowed)</p>
            <input type="number" inputMode="decimal" min="0" value={amt} onChange={e => setAmt(e.target.value)} style={{ ...inp, marginBottom: 8 }} data-testid="walkin-detail-amount" />
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              {['cash', 'gcash', 'card'].map(m => (
                <button key={m} onClick={() => setMethod(m)}
                  style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: `1.5px solid ${method === m ? '#00E676' : '#2A2A2A'}`, background: method === m ? '#00E67614' : '#0A0A0A', color: method === m ? '#00E676' : '#737373', fontWeight: 800, fontSize: 11, textTransform: 'uppercase', cursor: 'pointer' }}>
                  {m}
                </button>
              ))}
            </div>
            {method === 'cash' && (
              <>
                <p style={{ fontSize: 10, color: '#555', textTransform: 'uppercase', marginBottom: 4 }}>Cash handed over (optional — computes change)</p>
                <input type="number" inputMode="decimal" min="0" value={tendered} onChange={e => setTendered(e.target.value)} placeholder="e.g. 500" style={{ ...inp, marginBottom: 6 }} data-testid="walkin-detail-tendered" />
                {hasTendered && !shortCash && <p style={{ fontSize: 13, color: '#FFB300', fontWeight: 800, marginBottom: 6 }}>Change: ₱{change.toLocaleString()}</p>}
                {shortCash && <p style={{ fontSize: 12, color: '#FF2D55', marginBottom: 6 }}>Cash handed over is less than the amount.</p>}
              </>
            )}
            <button disabled={amount <= 0 || shortCash} onClick={() => setConfirming(true)} data-testid="walkin-detail-next"
              style={{ width: '100%', padding: 12, borderRadius: 9, background: amount > 0 && !shortCash ? '#00E676' : '#1A1A1A', border: 'none', color: amount > 0 && !shortCash ? '#000' : '#555', fontWeight: 900, fontSize: 13, textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5 }}>
              Review payment
            </button>
          </div>
        )}

        {collectable && confirming && (
          <div style={{ borderTop: '1px solid #1E1E1E', paddingTop: 12 }}>
            <div style={{ background: '#0A0A0A', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
              <p style={{ fontSize: 13, color: '#ccc', margin: '3px 0' }}>Record <b style={{ color: '#00E676' }}>₱{amount.toLocaleString()}</b> · {method.toUpperCase()} from <b style={{ color: '#fff' }}>{b.customer?.name}</b> — are you sure?</p>
              {hasTendered && <p style={{ fontSize: 13, color: '#ccc', margin: '3px 0' }}>Received ₱{tenderedNum.toLocaleString()} → give <b style={{ color: '#FFB300' }}>₱{change.toLocaleString()}</b> change</p>}
              {amount < balance && <p style={{ fontSize: 12, color: '#FF2D55', margin: '3px 0' }}>₱{(balance - amount).toLocaleString()} will remain owed</p>}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setConfirming(false)} style={{ flex: 1, padding: 11, borderRadius: 8, background: '#1A1A1A', border: '1px solid #2A2A2A', color: '#737373', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', cursor: 'pointer' }}>Back</button>
              <button disabled={busy} onClick={submit} data-testid="walkin-detail-confirm"
                style={{ flex: 2, padding: 11, borderRadius: 8, background: '#00E676', border: 'none', color: '#000', fontWeight: 900, fontSize: 12, textTransform: 'uppercase', cursor: busy ? 'wait' : 'pointer' }}>
                {busy ? 'Saving…' : 'Yes, record it'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SportsWalkInPage() {
  const navigate = useNavigate();
  const windowWidth = useWindowWidth();
  const isDesktop = windowWidth >= 960;

  // Facility timezone (fetched from settings)
  const [facilityTz, setFacilityTz] = useState('Asia/Manila');
  const todayInTz = ymdTz(facilityTz);

  // Facility-clock minute-of-day (30s ticker) — past slots dim but stay
  // tappable so the cashier can DELIBERATELY back-record a finished session
  // (owner 2026-08-17); the ticket shows an amber notice when they do.
  const [__nowTick, __setNowTick] = useState(0);
  useEffect(() => { const id = setInterval(() => __setNowTick(v => v + 1), 30000); return () => clearInterval(id); }, []);
  const facilityNowMin = (() => {
    void __nowTick;
    try {
      const parts = new Intl.DateTimeFormat('en-GB', { timeZone: facilityTz, hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(new Date());
      const g = (k) => parseInt(parts.find(x => x.type === k)?.value || '0', 10);
      return (g('hour') % 24) * 60 + g('minute');
    } catch { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); }
  })();
  const slotIsPastWalkin = (slot) => {
    if (!slot) return false;
    if (date > todayInTz) return false;
    if (date === todayInTz) return toMin(slot.end) <= facilityNowMin;
    if (date === addDaysYmd(todayInTz, -1)) return toMin(slot.end) <= facilityNowMin + 1440;
    return true;
  };

  // ── Walk-in booking state ───────────────────────────────────────
  const [courts, setCourts] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [date, setDate] = useState(todayInTz);
  const [slots, setSlots] = useState([]);
  const [slotLoading, setSlotLoading] = useState(false);
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [bookingType, setBookingType] = useState('walk_in');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymongoOn, setPaymongoOn] = useState(false);
  const [payLink, setPayLink] = useState(null);
  const [showPayLinkModal, setShowPayLinkModal] = useState(false);
  const [customer, setCustomer] = useState({ name: '', email: '', phone: '' });
  const [eventName, setEventName] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const [slug, setSlug] = useState('');
  const [amount, setAmount] = useState(0);
  const [playNow, setPlayNow] = useState(null);   // {available, start, slot_end, minutes, price, buffer_min}
  const [pnMode, setPnMode] = useState(false);     // Play Now selected instead of a manual range

  // ── Equipment Rentals ──────────────────────────────────────────
  const [equipmentAvail, setEquipmentAvail] = useState(null);
  const [equipLoading, setEquipLoading] = useState(false);
  const [selectedRentals, setSelectedRentals] = useState({});

  // ── Discount ───────────────────────────────────────────────────
  const [discountType, setDiscountType] = useState('percent'); // 'percent' | 'flat'
  const [discountValue, setDiscountValue] = useState('');
  // ── Voucher ────────────────────────────────────────────────────
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherInfo, setVoucherInfo] = useState(null);   // validate response
  const [voucherErr, setVoucherErr] = useState('');
  const [voucherBusy, setVoucherBusy] = useState(false);

  // ── Find Booking / Collect Payment state ───────────────────────
  const [findMode, setFindMode] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchDate, setSearchDate] = useState('');   // empty = ALL dates (unique codes must always resolve)
  const [searchExtras, setSearchExtras] = useState(null); // {players, teams} from universal search
  const [detailBooking, setDetailBooking] = useState(null); // booking open in the detail modal
  const [balancesOpen, setBalancesOpen] = useState(false);  // "open balances today" panel
  const [openBalances, setOpenBalances] = useState(null);   // today's confirmed bookings still owing

  const loadOpenBalances = async (tz) => {
    try {
      const r = await sportsApi.get('/sports/bookings',
        { params: { date: ymdTz(tz || facilityTz), status: 'confirmed' } });
      const rows = (r.data || [])
        .filter(b => b.payment_status !== 'paid'
          && (b.balance_due != null
            ? (parseFloat(b.balance_due) || 0) > 0.005
            : (parseFloat(b.amount) || 0) - (parseFloat(b.amount_paid) || 0) > 0.005))
        .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
      setOpenBalances(rows);
    } catch { /* pill just stays count-less */ }
  };
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    sportsApi.get('/sports/courts')
      .then(r => { const active = (r.data || []).filter(c => c.active); setCourts(active); if (active.length > 0) setSelectedCourt(active[0]); })
      .catch(() => {});
    sportsApi.get('/sports/auth/me').then(r => setSlug(r.data?.organization?.slug || '')).catch(() => {});
    // Fetch timezone from settings
    sportsApi.get('/sports/settings').then(r => {
      const tz = r.data?.timezone || 'Asia/Manila';
      setFacilityTz(tz);
      const today = ymdTz(tz);
      setDate(today);
      // NOTE: searchDate intentionally NOT set — empty means the desk search
      // covers ALL dates; unique codes must always resolve.
      setPaymongoOn(!!(r.data?.paymongo?.connected));
      loadOpenBalances(tz);
    }).catch(() => {});
  }, []);

  // Load slots for the chosen court/date. `silent` skips the spinner, view-log,
  // and error-wipe — used by the background refreshers so they repaint cells
  // without disturbing an in-progress selection.
  const loadSlots = useCallback(({ silent = false } = {}) => {
    if (!selectedCourt || !date || !slug) return;
    if (!silent) setSlotLoading(true);
    // log_view: false ALWAYS — staff browsing must never count as customer
    // views in the public page's urgency signals.
    axios.get(`${BACKEND}/api/sports/public/${slug}/availability`, { params: { court_id: selectedCourt.id, date, log_view: false } })
      .then(r => { setSlots(r.data?.slots || []); setPlayNow(r.data?.play_now || null); if (!silent) setPnMode(false); })
      .catch(() => { if (!silent) { setSlots([]); setPlayNow(null); setPnMode(false); } })
      .finally(() => { if (!silent) setSlotLoading(false); });
  }, [selectedCourt, date, slug]);

  // Court/date changed → clear any in-progress pick and (re)load availability.
  useEffect(() => {
    if (!selectedCourt || !date || !slug) return;
    setRangeStart(null); setRangeEnd(null);
    loadSlots();
  }, [loadSlots]); // eslint-disable-line react-hooks/exhaustive-deps

  // Quiet live refresh: pick up OTHER staff/customer bookings without a reload —
  // ONLY while idly viewing the grid. Suspends during a selection, the
  // confirmation screen, Find-booking mode, or when the tab is hidden.
  useEffect(() => {
    if (!selectedCourt || !date || !slug) return;
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (done || findMode) return;
      if (rangeStart !== null || rangeEnd !== null || pnMode) return;
      loadSlots({ silent: true });
    }, 15000);
    return () => clearInterval(id);
  }, [selectedCourt, date, slug, done, findMode, rangeStart, rangeEnd, pnMode, loadSlots]);

  const changeDate = (delta) => {
    setDate(addDaysYmd(date, delta));
  };

  const handleSlotClick = (i) => {
    if (pnMode) {
      const si = slots.findIndex(s => s.end === playNow?.slot_end);
      if (si < 0) return;
      if (i <= si) { // tapped at/before the in-progress slot → exit Play Now, start a normal selection
        setPnMode(false);
        if (slots[i]?.available) { setRangeStart(i); setRangeEnd(null); }
        return;
      }
      if (slots.slice(si, i).every(s => s.available)) { setRangeStart(si); setRangeEnd(i); }
      return;
    }
    // Strict cycle: 1st tap = start · 2nd tap = end · 3rd tap = cancel (then restart)
    if (rangeStart !== null && rangeEnd !== null) { setRangeStart(null); setRangeEnd(null); return; }
    if (rangeStart === null) { if (!slots[i]?.available) return; setRangeStart(i); setRangeEnd(null); return; }
    if (i === rangeStart) { setRangeStart(null); setRangeEnd(null); return; }
    if (i < rangeStart) { if (slots[i]?.available) setRangeStart(i); return; }
    if (slots.slice(rangeStart, i).every(s => s.available)) setRangeEnd(i);
  };

  const activatePlayNow = () => {
    if (!playNow) return;
    const si = slots.findIndex(s => s.end === playNow.slot_end);
    if (si < 0) return;
    setRangeStart(si); setRangeEnd(si + 1);
    setPnMode(true);
  };

  const pointTime = (idx) => idx == null ? null : (idx < slots.length ? slots[idx]?.start : slots[slots.length - 1]?.end);
  const [quoteErr, setQuoteErr] = useState('');
  const [conflictAlts, setConflictAlts] = useState(null);
  // Free intervals per court for the selected date — counter glance (adapt batch)
  const [courtGlance, setCourtGlance] = useState({});
  useEffect(() => {
    if (!slug || !courts.length || !date) return;
    let gone = false;
    Promise.all(courts.map(c =>
      axios.get(`${BACKEND}/api/sports/public/${slug}/availability`,
        { params: { court_id: c.id, date, log_view: false } })
        .then(r => ({ id: c.id, open: r.data.open, ivs: r.data.free_intervals || [] }))
        .catch(() => null)
    )).then(rs => {
      if (gone) return;
      const m = {};
      rs.filter(Boolean).forEach(r => { m[r.id] = r; });
      setCourtGlance(m);
    });
    return () => { gone = true; };
  }, [slug, courts, date]);

  // Clear stale conflict offers whenever the selection context changes
  useEffect(() => { setConflictAlts(null); }, [selectedCourt?.id, date, rangeStart, rangeEnd]);

  const [pendingTimes, setPendingTimes] = useState(null); // {start, end}
  useEffect(() => {
    if (!pendingTimes || !slots.length) return;
    const si = slots.findIndex(x => x.start === pendingTimes.start);
    if (si >= 0 && slots[si].available) {
      setRangeStart(si);
      const ei = slots.findIndex(x => x.start === pendingTimes.end);
      setRangeEnd(ei >= 0 ? ei : (slots[slots.length - 1]?.end === pendingTimes.end ? slots.length : null));
    }
    setPendingTimes(null);
  }, [slots, pendingTimes]);

  const selectedStart = pnMode ? (playNow?.start || null) : (rangeStart !== null ? slots[rangeStart]?.start : null);
  const selectedEnd   = pnMode
    ? (rangeEnd !== null ? pointTime(rangeEnd) : (playNow?.slot_end || null))
    : (rangeEnd !== null ? pointTime(rangeEnd) : null);

  useEffect(() => {
    if (!selectedCourt || !selectedStart || !selectedEnd || !slug) { setAmount(0); setEquipmentAvail(null); setSelectedRentals({}); return; }
    if (pnMode) {
      axios.get(`${BACKEND}/api/sports/public/${slug}/quote`, {
        params: { court_id: selectedCourt.id, date, start: selectedStart, end: selectedEnd, play_now: true }
      })
        .then(r => setAmount(r.data?.amount ?? (playNow?.price || 0)))
        .catch(() => setAmount(playNow?.price || 0));
    } else {
      axios.get(`${BACKEND}/api/sports/public/${slug}/quote`, {
        params: { court_id: selectedCourt.id, date, start: selectedStart, end: selectedEnd }
      })
        .then(r => { setAmount(r.data?.amount ?? 0); setQuoteErr(''); })
        .catch(e => {
          setAmount(0);
          const d = e.response?.data?.detail;
          setQuoteErr(typeof d === 'string' ? d : (d?.message || ''));
        });
    }
    // Load equipment availability for selected time window
    setEquipLoading(true); setEquipmentAvail(null); setSelectedRentals({});
    axios.get(`${BACKEND}/api/sports/public/${slug}/equipment-availability`, {
      params: { sport: selectedCourt.sport, date, start_time: selectedStart, end_time: selectedEnd }
    })
      .then(r => setEquipmentAvail(r.data?.equipment || []))
      .catch(() => setEquipmentAvail([]))
      .finally(() => setEquipLoading(false));
  }, [selectedStart, selectedEnd, selectedCourt, date, slug, pnMode]);

  const applyVoucher = async () => {
    const code = voucherCode.trim().toUpperCase();
    if (!code) return;
    if (!selectedStart || !selectedEnd) { setVoucherErr('Select a time slot first'); return; }
    setVoucherBusy(true); setVoucherErr(''); setVoucherInfo(null);
    try {
      const r = await axios.post(`${BACKEND}/api/sports/public/${slug}/voucher/validate`, {
        code, court_id: selectedCourt.id, date, start_time: selectedStart, end_time: selectedEnd, play_now: pnMode,
      });
      setVoucherInfo(r.data);
      toast.success('Voucher applied');
    } catch (e) {
      const d = e.response?.data?.detail;
      setVoucherErr(typeof d === 'object' ? d.message : (d || 'Invalid voucher'));
    } finally { setVoucherBusy(false); }
  };

  const clearVoucher = () => { setVoucherCode(''); setVoucherInfo(null); setVoucherErr(''); };

  // Voucher validity depends on the chosen window — clear it if the selection changes
  useEffect(() => { setVoucherInfo(null); setVoucherErr(''); }, [selectedStart, selectedEnd, selectedCourt]);

  const handleBook = async () => {
    if (!selectedCourt || !selectedStart || !selectedEnd) { toast.error('Select a court and time range'); return; }
    if (bookingType === 'walk_in' && !customer.name.trim()) { toast.error('Customer name is required'); return; }
    if (bookingType === 'competition' && !eventName.trim()) { toast.error('Event name is required'); return; }
    setSaving(true);

    // Build rentals payload
    const rentalItems = Object.entries(selectedRentals)
      .filter(([, qty]) => qty > 0)
      .map(([eqId, qty]) => {
        const eq = (equipmentAvail || []).find(e => e.equipment_id === eqId);
        const window = (eq?.windows || []).find(w => w.available_qty >= qty);
        return {
          equipment_id: eqId,
          name: eq?.name || eqId,
          qty,
          unit: eq?.unit || 'piece',
          start_time: window?.start || selectedStart,
          end_time: window?.end || selectedEnd,
        };
      });

    try {
      // PayMongo is a UI-only convenience: the backend saves it as an unpaid
      // (floating) booking, then we auto-open the Full/50%/Custom link modal.
      const effectivePayMethod = paymentMethod === 'paymongo' ? 'unpaid' : paymentMethod;
      const res = await sportsApi.post('/sports/walk-in', {
        court_id: selectedCourt.id, date, start_time: selectedStart, end_time: selectedEnd,
        booking_type: bookingType, payment_method: effectivePayMethod,
        customer: bookingType === 'walk_in' ? customer : { name: `Competition: ${eventName}` },
        event_name: eventName, notes,
        rentals: rentalItems,
        play_now: pnMode,
        voucher_code: voucherInfo ? voucherInfo.code : '',
        discount_type: voucherInfo ? '' : (discountValue ? discountType : ''),
        discount_value: voucherInfo ? 0 : (discountValue ? parseFloat(discountValue) : 0),
      });
      setDone({ bookingId: res.data.booking_id, refNumber: res.data.ref_number, amount: res.data.amount, discountAmount: res.data.discount_amount || 0, rentals: rentalItems, paymentMethod: effectivePayMethod });
      // Self-update: refresh the grid behind the confirmation so the just-booked
      // slots are already crossed out when the cashier taps "New Booking".
      loadSlots({ silent: true });
      // If the user picked PayMongo, jump straight into the Full/50%/Custom picker.
      if (paymentMethod === 'paymongo') {
        setShowPayLinkModal(true);
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === 'slot_taken' && d?.alternatives) {
        setConflictAlts(d.alternatives);
        toast.error(d.message || 'That time was just taken — see the options below the grid');
      } else {
        toast.error(typeof d === 'object' ? d.message : (d || 'Booking failed'));
      }
    }
    finally { setSaving(false); }
  };

  const resetAll = () => {
    setDone(null); setRangeStart(null); setRangeEnd(null); setPnMode(false);
    setCustomer({ name: '', email: '', phone: '' }); setEventName(''); setNotes(''); setAmount(0);
    setEquipmentAvail(null); setSelectedRentals({});
    setDiscountValue(''); clearVoucher(); setPayLink(null);
  };

  const generatePayLink = async (amountChoice = 'full') => {
    if (!done?.bookingId) return;
    try {
      const r = await sportsApi.post(`/sports/bookings/${done.bookingId}/payment-link`, { amount_choice: amountChoice, origin: 'walkin' });
      setPayLink(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'object' ? d.message : (d || 'Could not create payment link'));
    }
  };

  // ── Find Booking / Collect Payment ───────────────────────────
  // The search box doubles as the QR target: a USB scanner types the QR's URL
  // into it. Booking QRs are handled by the backend search; player/team QRs
  // belong to the scanner's mini-POS card, so we hand those off.
  const handleSearch = async () => {
    const text = searchQ.trim();
    if (/\/sports\/open-play\/(?:[\w-]+\/)?player\/[\w-]+/.test(text)
        || /\/sports\/tournament\/[\w-]+\/team\/[\w-]+/.test(text)) {
      navigate(`/sports/scan${slug ? `/${slug}` : ''}?code=${encodeURIComponent(text)}`);
      return;
    }
    setSearching(true); setSearchResults(null); setSearchExtras(null);
    try {
      const res = await sportsApi.get('/sports/scan/search', {
        params: { q: text, date: searchDate || undefined }
      });
      setSearchResults(res.data?.bookings || []);
      setSearchExtras({ players: res.data?.players || [], teams: res.data?.teams || [] });
    } catch { toast.error('Search failed'); }
    finally { setSearching(false); }
  };

  const openInScanner = (code) =>
    navigate(`/sports/scan${slug ? `/${slug}` : ''}?code=${encodeURIComponent(code)}`);

  // Payment recording lives in WalkinBookingModal — always amount + double-confirm,
  // never a one-tap "paid".
  const handleModalPaid = (upd) => {
    setDetailBooking(prev => prev ? { ...prev, ...upd } : prev);
    setSearchResults(prev => (prev || []).map(x =>
      detailBooking && x.id === detailBooking.id ? { ...x, ...upd } : x));
    setOpenBalances(prev => (prev || [])
      .map(x => detailBooking && x.id === detailBooking.id ? { ...x, ...upd } : x)
      .filter(x => x.payment_status !== 'paid' && (parseFloat(x.balance_due) || 0) > 0.005));
  };

  // ── Confirmed screen ──────────────────────────────────────────
  if (done) return (
    <div className="min-h-screen" style={{ background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
      <SportsNavBar />
      <main className="max-w-md mx-auto px-5 py-20 text-center">
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#00E67614', border: '2px solid #00E676', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <CheckCircle className="h-10 w-10" style={{ color: '#00E676' }} />
        </div>
        <h1 className="text-3xl font-bold uppercase mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>Confirmed!</h1>
        {done.refNumber && (
          <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, fontWeight: 900, color: '#E63946', letterSpacing: 3, marginBottom: 4 }}>{done.refNumber}</p>
        )}
        <p style={{ color: '#737373', marginBottom: 4 }}>Booking ID: <span style={{ color: '#fff', fontFamily: 'monospace' }}>{done.bookingId.slice(0, 8)}…</span></p>
        {done.discountAmount > 0 && (
          <p style={{ color: '#737373', marginBottom: 2, fontSize: 13 }}>
            Discount saved: <span style={{ color: '#00E676', fontWeight: 700 }}>−₱{done.discountAmount.toLocaleString()}</span>
          </p>
        )}
        <p className="text-4xl font-black mb-8" style={{ color: '#00E676', fontFamily: 'Oswald, sans-serif' }}>₱{done.amount?.toLocaleString()}</p>
        {selectedCourt && <p style={{ color: '#A3A3A3', marginBottom: 12, fontSize: 14 }}>{selectedCourt.name} · {fmtDateShort(date)} · {fmt12(selectedStart)}–{fmt12(selectedEnd)}</p>}
        {done.rentals && done.rentals.length > 0 && (
          <div style={{ background: '#0A1A10', border: '1px solid #00E67630', borderRadius: 10, padding: '12px 14px', marginBottom: 20, textAlign: 'left' }}>
            <p style={{ fontSize: 10, color: '#00E676', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Rented Equipment — Issue Now</p>
            {done.rentals.map((r, i) => (
              <p key={i} style={{ fontSize: 13, color: '#ccc', marginBottom: 3 }}>• {r.qty}× {r.name} ({r.unit})</p>
            ))}
          </div>
        )}
        {/* Generate PayMongo payment link (QR Ph / GCash / e-wallet) — any confirmed booking */}
        {paymongoOn && done?.bookingId && done?.paymentMethod !== 'paid' && (
          <div style={{ marginBottom: 20 }}>
            {!payLink ? (
              <button onClick={() => setShowPayLinkModal(true)} data-testid="walkin-gen-paylink-btn"
                style={{ width: '100%', padding: '12px', borderRadius: 10, border: '1.5px solid #00E67655', background: '#00E67614', color: '#00E676', fontWeight: 800, textTransform: 'uppercase', fontSize: 13, cursor: 'pointer', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5 }}>
                Generate PayMongo Link (Full / 50% / Custom)
              </button>
            ) : (
              <div data-testid="walkin-paylink-result" style={{ background: '#0A1A10', border: '1px solid #00E67630', borderRadius: 12, padding: 16 }}>
                <p style={{ fontSize: 10, color: '#00E676', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Scan to pay ₱{Number(payLink.total).toLocaleString()}{payLink.service_fee > 0 ? ' (incl. fee)' : ''}</p>
                <div style={{ display: 'inline-block', background: '#fff', padding: 10, borderRadius: 10 }}>
                  <QRCodeSVG value={payLink.checkout_url} size={168} />
                </div>
                <p style={{ fontSize: 11, color: '#8a8a8a', margin: '10px 0 6px' }}>Customer scans with any bank / e-wallet app, or open the link:</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <a href={payLink.checkout_url} target="_blank" rel="noreferrer" data-testid="walkin-paylink-open"
                    style={{ flex: 1, padding: '9px', borderRadius: 8, border: '1px solid #2A2A2A', background: '#111', color: '#ccc', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>Open Link</a>
                  <button onClick={() => { navigator.clipboard?.writeText(payLink.checkout_url); toast.success('Link copied'); }}
                    style={{ flex: 1, padding: '9px', borderRadius: 8, border: '1px solid #2A2A2A', background: '#111', color: '#ccc', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Copy Link</button>
                </div>
                <p style={{ fontSize: 10, color: '#555', marginTop: 8 }}>Payment confirms automatically once the customer completes it.</p>
              </div>
            )}
          </div>
        )}
        <PaymentLinkModal
          open={showPayLinkModal}
          onClose={() => setShowPayLinkModal(false)}
          title="Generate Payment Link"
          subtitle={done?.amount ? `Booking total ₱${Number(done.amount).toLocaleString()}` : ''}
          baseAmount={done?.amount}
          onGenerate={async ({ amount_choice, amount_php }) => {
            const r = await sportsApi.post(
              `/sports/bookings/${done.bookingId}/payment-link`,
              { amount_choice, amount_php, origin: 'walkin' });
            setPayLink(r.data);
            return r.data;
          }}
        />
        <div className="flex gap-3 justify-center">
          <button onClick={resetAll} className="px-6 py-3 rounded-xl font-bold uppercase text-sm" style={{ background: '#E63946', color: '#fff', fontFamily: 'Oswald, sans-serif', letterSpacing: 1 }}>
            Next Customer
          </button>
          <button onClick={() => navigate('/sports/bookings')} className="px-6 py-3 rounded-xl font-bold uppercase text-sm" style={{ background: '#1A1A1A', color: '#A3A3A3', border: '1px solid #2A2A2A' }}>
            View Bookings
          </button>
        </div>
      </main>
    </div>
  );

  // ── Slot grid helpers ─────────────────────────────────────────
  // Group slots by hour for cleaner display on desktop
  const groupedSlots = slots.reduce((acc, slot, i) => {
    const hour = parseInt(slot.start.split(':')[0], 10);
    const key = `${hour}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push({ ...slot, idx: i });
    return acc;
  }, {});

  const slotGrid = ({ compact = false }) => (
    <div>
      {!selectedCourt && (
        <div style={{ textAlign: 'center', padding: compact ? '20px 0' : '40px 0', color: '#333' }}>
          <div style={{ fontSize: compact ? 32 : 48, marginBottom: 8 }}>🏟</div>
          <p style={{ fontSize: compact ? 12 : 14 }}>Select a court above to see available slots</p>
        </div>
      )}
      {selectedCourt && slotLoading && (
        <div style={{ textAlign: 'center', padding: compact ? '20px 0' : '40px 0' }}>
          <div className="animate-spin" style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid #E63946', borderTopColor: 'transparent', margin: '0 auto 8px' }} />
          <p style={{ fontSize: 12, color: '#555' }}>Loading slots…</p>
        </div>
      )}
      {selectedCourt && !slotLoading && slots.length > 0 && (
        <div>
          {/* ── Play Now: instant pro-rated booking of the in-progress slot ── */}
          {playNow?.available && (
            <button
              data-testid="walkin-play-now-btn"
              onClick={activatePlayNow}
              style={{
                width: '100%', marginBottom: compact ? 10 : 14,
                padding: compact ? '10px 12px' : '12px 16px', borderRadius: 10,
                border: pnMode ? '2px solid #00E676' : '1.5px solid #00E67655',
                background: pnMode ? '#00E67618' : 'linear-gradient(90deg,#00E67610,#00E6760a)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                textAlign: 'left', transition: 'all 0.15s',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Zap style={{ width: 18, height: 18, color: '#00E676' }} />
                <div>
                  <p style={{ fontSize: compact ? 12 : 13, fontWeight: 800, color: '#00E676', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Play Now {pnMode && '· Selected'}
                  </p>
                  <p style={{ fontSize: 10, color: '#8a8a8a' }}>
                    {pnMode
                      ? 'Tap a later slot to play longer (extra hours at standard rate)'
                      : `Start ${fmt12(playNow.start)} – ${fmt12(playNow.slot_end)} · ${playNow.minutes} min play`}
                  </p>
                </div>
              </div>
              <span style={{ fontFamily: 'Oswald, sans-serif', fontSize: compact ? 16 : 18, fontWeight: 900, color: '#00E676' }}>
                ₱{Number((pnMode ? amount : playNow.price) || 0).toLocaleString()}
              </span>
            </button>
          )}
          {/* Instruction hint */}
          {rangeStart === null ? (
            <p style={{ fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: 1, marginBottom: compact ? 10 : 14 }}>
              Tap start time → tap end time
            </p>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: compact ? 10 : 14, flexWrap: 'wrap', gap: 6 }}>
              <p style={{ fontSize: 11, color: '#E63946', textTransform: 'uppercase', letterSpacing: 1 }}>
                {selectedEnd ? `${fmt12(selectedStart)} – ${fmt12(selectedEnd)}` : `From ${fmt12(selectedStart)} → tap end time`}
                {quoteErr && (
                  <span style={{ display: 'block', color: '#FF2D55', letterSpacing: 0.4, marginTop: 3, textTransform: 'none', fontWeight: 700 }}>
                    ⏱ {quoteErr}
                  </span>
                )}
                {(date < todayInTz || (date === todayInTz && toMin(selectedStart) < facilityNowMin)) && (
                  <span data-testid="walkin-past-notice-inline" style={{ display: 'block', color: '#F59E0B', letterSpacing: 0.4, marginTop: 3, textTransform: 'none', fontWeight: 700 }}>
                    ⏱ This time has already passed — recording a past walk-in
                  </span>
                )}
              </p>
              <button onClick={() => { setRangeStart(null); setRangeEnd(null); }}
                style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}>
                <X style={{ width: 12, height: 12 }} /> Clear
              </button>
            </div>
          )}

          {conflictAlts && (
            <div data-testid="walkin-conflict-alts" style={{ background: '#FF2D5510', border: '1px solid #FF2D5540', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
              <p style={{ fontSize: 11, color: '#FF8080', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>That time was taken — options:</p>
              {(conflictAlts.same_day_courts || []).map((sc, i) => (
                <p key={`sc-${i}`} style={{ fontSize: 12, color: '#A3A3A3', marginTop: 4 }}>
                  {sc.court_name}: {fmt12(sc.start)} – {fmt12(sc.end)} · ₱{Number(sc.amount || 0).toLocaleString()}
                  <button data-testid={`walkin-alt-court-${i}`} onClick={() => {
                    const c = courts.find(x => x.id === sc.court_id);
                    if (c) { setSelectedCourt(c); setPendingTimes({ start: sc.start, end: sc.end }); setConflictAlts(null); }
                  }} style={{ marginLeft: 8, background: 'none', border: 'none', color: '#E63946', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Use</button>
                </p>
              ))}
              {conflictAlts.same_day_partial && (
                <p style={{ fontSize: 12, color: '#A3A3A3', marginTop: 4 }}>
                  Same court: {fmt12(conflictAlts.same_day_partial.start)} – {fmt12(conflictAlts.same_day_partial.end)}
                  <button data-testid="walkin-alt-partial" onClick={() => { setPendingTimes({ start: conflictAlts.same_day_partial.start, end: conflictAlts.same_day_partial.end }); setConflictAlts(null); }}
                    style={{ marginLeft: 8, background: 'none', border: 'none', color: '#E63946', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Use</button>
                </p>
              )}
              {(conflictAlts.next_dates || []).slice(0, 3).map((nd, i) => (
                <p key={`nd-${i}`} style={{ fontSize: 12, color: '#A3A3A3', marginTop: 4 }}>
                  {nd.date}: {fmt12(nd.start)} – {fmt12(nd.end)}{nd.court_name ? ` · ${nd.court_name}` : ''}
                  <button onClick={() => {
                    const c = nd.court_id ? courts.find(x => x.id === nd.court_id) : null;
                    if (c) setSelectedCourt(c);
                    setDate(nd.date); setPendingTimes({ start: nd.start, end: nd.end }); setConflictAlts(null);
                  }} style={{ marginLeft: 8, background: 'none', border: 'none', color: '#E63946', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Use</button>
                </p>
              ))}
            </div>
          )}
          {compact ? (
            /* ── Mobile / compact: responsive grid with shorter labels ── */
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(78px, 1fr))', gap: 4 }}>
              {slots.map((slot, i) => {
                const inRange = rangeStart !== null && rangeEnd !== null && i >= rangeStart && i <= rangeEnd;
                const isStart = i === rangeStart;
                const isEnd   = i === rangeEnd;
                const isTaken = !slot.available;
                const isPast = !isTaken && slotIsPastWalkin(slot);
                // Picking an end (start chosen, end not yet): the first taken slot
                // after the free run is a legal END tap — don't paint it as blocked.
                const boundaryHint = isTaken && !pnMode && rangeStart !== null && rangeEnd === null
                  && i > rangeStart && slots.slice(rangeStart, i).every(s => s.available);
                return (
                  <button key={i} onClick={() => handleSlotClick(i)}
                    disabled={isTaken && rangeStart === null}
                    title={boundaryHint ? `You can end here — the next booking starts at ${fmt12(slot.start)}` : undefined}
                    data-testid={`walkin-slot-${i}`}
                    style={{
                      height: 40, borderRadius: 7,
                      border: isStart || isEnd ? '2px solid #E63946' : inRange ? '1.5px solid #E6394640' : boundaryHint ? '1.5px dashed #E63946' : isTaken ? '1px solid #111' : '1px solid #2A2A2A',
                      background: isStart || isEnd ? '#E63946' : inRange ? '#E6394618' : boundaryHint ? '#E6394612' : isTaken ? '#0D0D0D' : '#1A1A1A',
                      color: isStart || isEnd ? '#fff' : inRange ? '#E63946' : boundaryHint ? '#E63946' : isTaken ? '#333' : isPast ? '#4A4A4A' : '#aaa',
                      opacity: isPast && !inRange && !isStart && !isEnd ? 0.55 : 1,
                      cursor: isTaken && rangeStart === null ? 'not-allowed' : 'pointer',
                      fontSize: 10, fontWeight: inRange || isStart || isEnd || boundaryHint ? 700 : 500,
                      fontFamily: 'Oswald, sans-serif', letterSpacing: 0.2,
                      transition: 'all 0.1s', position: 'relative', overflow: 'hidden',
                    }}>
                    {isTaken && !boundaryHint && <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 1, background: '#282828', transform: 'translateY(-50%) rotate(-6deg)' }} />}
                    {fmt12(slot.start)}
                  </button>
                );
              })}
              {slots.length > 0 && (() => {
                const N = slots.length;
                const isEnd = rangeEnd === N;
                const disabled = rangeStart === null && !pnMode;
                return (
                  <button key="end-cap" data-testid="walkin-slot-end" title="End of day (closing time)"
                    disabled={disabled}
                    onClick={() => {
                      if (rangeEnd !== null && !pnMode) { setRangeStart(null); setRangeEnd(null); return; }
                      const start = pnMode ? slots.findIndex(s => s.end === playNow?.slot_end) : rangeStart;
                      if (start == null || start < 0) return;
                      if (slots.slice(start, N).every(s => s.available)) setRangeEnd(N);
                    }}
                    style={{
                      height: 40, borderRadius: 7,
                      border: isEnd ? '2px solid #E63946' : '1px dashed #2A2A2A',
                      background: isEnd ? '#E63946' : '#1A1A1A',
                      color: isEnd ? '#fff' : '#aaa',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      fontSize: 10, fontWeight: isEnd ? 700 : 500,
                      fontFamily: 'Oswald, sans-serif', letterSpacing: 0.2, opacity: disabled ? 0.4 : 1,
                    }}>
                    {fmt12(slots[N - 1].end)}
                  </button>
                );
              })()}
            </div>
          ) : (
            /* ── Desktop: flat grid — wider labeled cards filling the full width ── */
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 6 }}>
              {slots.map((slot, i) => {
                const inRange = rangeStart !== null && rangeEnd !== null && i >= rangeStart && i <= rangeEnd;
                const isStart = i === rangeStart;
                const isEnd   = i === rangeEnd;
                const isTaken = !slot.available;
                const isPast = !isTaken && slotIsPastWalkin(slot);
                // Picking an end (start chosen, end not yet): the first taken slot
                // after the free run is a legal END tap — don't paint it as blocked.
                const boundaryHint = isTaken && !pnMode && rangeStart !== null && rangeEnd === null
                  && i > rangeStart && slots.slice(rangeStart, i).every(s => s.available);
                return (
                  <button key={i} onClick={() => handleSlotClick(i)}
                    disabled={isTaken && rangeStart === null}
                    title={boundaryHint ? `You can end here — the next booking starts at ${fmt12(slot.start)}` : undefined}
                    data-testid={`walkin-slot-${i}`}
                    style={{
                      height: 52, borderRadius: 8,
                      border: isStart || isEnd ? '2px solid #E63946' : inRange ? '1.5px solid #E6394640' : boundaryHint ? '1.5px dashed #E63946' : isTaken ? '1px solid #111' : '1px solid #2A2A2A',
                      background: isStart || isEnd ? '#E63946' : inRange ? '#E6394618' : boundaryHint ? '#E6394612' : isTaken ? '#0D0D0D' : '#1A1A1A',
                      color: isStart || isEnd ? '#fff' : inRange ? '#E63946' : boundaryHint ? '#E63946' : isTaken ? '#333' : isPast ? '#4A4A4A' : '#aaa',
                      opacity: isPast && !inRange && !isStart && !isEnd ? 0.55 : 1,
                      cursor: isTaken && rangeStart === null ? 'not-allowed' : 'pointer',
                      fontSize: 12, fontWeight: inRange || isStart || isEnd || boundaryHint ? 700 : 500,
                      fontFamily: 'Oswald, sans-serif', letterSpacing: 0.3,
                      transition: 'all 0.12s', position: 'relative', overflow: 'hidden',
                    }}
                    onMouseEnter={e => { if (!isTaken && !inRange && !isStart && !isEnd) e.currentTarget.style.background = '#242424'; }}
                    onMouseLeave={e => { if (!isTaken && !inRange && !isStart && !isEnd) e.currentTarget.style.background = boundaryHint ? '#E6394612' : '#1A1A1A'; }}>
                    {isTaken && !boundaryHint && <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 1, background: '#282828', transform: 'translateY(-50%) rotate(-6deg)' }} />}
                    {fmt12(slot.start)}
                  </button>
                );
              })}
              {slots.length > 0 && (() => {
                const N = slots.length;
                const isEnd = rangeEnd === N;
                const disabled = rangeStart === null && !pnMode;
                return (
                  <button key="end-cap" data-testid="walkin-slot-end-desktop" title="End of day (closing time)"
                    disabled={disabled}
                    onClick={() => {
                      if (rangeEnd !== null && !pnMode) { setRangeStart(null); setRangeEnd(null); return; }
                      const start = pnMode ? slots.findIndex(s => s.end === playNow?.slot_end) : rangeStart;
                      if (start == null || start < 0) return;
                      if (slots.slice(start, N).every(s => s.available)) setRangeEnd(N);
                    }}
                    style={{
                      height: 52, borderRadius: 8,
                      border: isEnd ? '2px solid #E63946' : '1px dashed #2A2A2A',
                      background: isEnd ? '#E63946' : '#1A1A1A',
                      color: isEnd ? '#fff' : '#aaa',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      fontSize: 12, fontWeight: isEnd ? 700 : 500,
                      fontFamily: 'Oswald, sans-serif', letterSpacing: 0.3, opacity: disabled ? 0.4 : 1,
                    }}>
                    {fmt12(slots[N - 1].end)}
                  </button>
                );
              })()}
            </div>
          )}

          {/* Pricing summary when range selected */}
          {selectedStart && selectedEnd && amount > 0 && (
            <div style={{ marginTop: compact ? 12 : 16, padding: compact ? '10px 14px' : '12px 16px', borderRadius: 10, background: '#E6394608', border: '1px solid #E6394630', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: 12, color: '#E63946', fontWeight: 700 }}>{fmt12(selectedStart)} – {fmt12(selectedEnd)}</p>
                <p style={{ fontSize: 10, color: '#555' }}>
                  {Math.round((toMin(selectedEnd) - toMin(selectedStart)) / 60 * 10) / 10}hr · {selectedCourt?.name}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                {(() => {
                  const durationHrs = (toMin(selectedEnd) - toMin(selectedStart)) / 60;
                  const rentalTotal = Object.entries(selectedRentals).filter(([,q]) => q > 0).reduce((sum, [id, q]) => {
                    const eq = (equipmentAvail || []).find(e => e.equipment_id === id);
                    if (!eq || !eq.rental_price) return sum;
                    return sum + (eq.rental_price_type === 'per_hour' ? eq.rental_price * q * durationHrs : eq.rental_price * q);
                  }, 0);
                  const grandTotal = amount + rentalTotal;
                  return (
                    <>
                      {rentalTotal > 0 && <p style={{ fontSize: 10, color: '#737373' }}>Court ₱{amount.toLocaleString()} + Rental ₱{rentalTotal.toLocaleString()}</p>}
                      <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 22, fontWeight: 900, color: '#E63946', lineHeight: 1.1 }}>₱{grandTotal.toLocaleString()}</p>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ── Customer + payment form ────────────────────────────────────
  const inputStyle = {
    background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff',
    borderRadius: 8, padding: '9px 12px', width: '100%', outline: 'none',
    fontFamily: 'Manrope, sans-serif', fontSize: 14, boxSizing: 'border-box',
  };

  const bookingForm = () => (
    <>
      {/* Booking type */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
        {[
          { id: 'walk_in', label: 'Walk-in', Icon: UserPlus, color: '#E63946' },
          { id: 'competition', label: 'Event', Icon: Trophy, color: '#FF6D00' },
        ].map(({ id, label, Icon, color }) => (
          <button key={id} onClick={() => setBookingType(id)}
            style={{ padding: '10px', borderRadius: 10, border: `1.5px solid ${bookingType === id ? color : '#2A2A2A'}`, background: bookingType === id ? color + '14' : '#0A0A0A', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon style={{ width: 16, height: 16, color: bookingType === id ? color : '#555' }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: bookingType === id ? color : '#737373', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
          </button>
        ))}
      </div>

      {/* Customer or event name */}
      {bookingType === 'walk_in' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
          <input value={customer.name} onChange={e => setCustomer(p => ({ ...p, name: e.target.value }))}
            placeholder="Customer name *" style={inputStyle} data-testid="sports-walkin-customer-name" />
          <input value={customer.phone} onChange={e => setCustomer(p => ({ ...p, phone: e.target.value }))}
            placeholder="Phone number" style={inputStyle} data-testid="sports-walkin-customer-phone" />
        </div>
      ) : (
        <div style={{ marginBottom: 14 }}>
          <input value={eventName} onChange={e => setEventName(e.target.value)}
            placeholder="Event / competition name *" style={inputStyle} data-testid="sports-walkin-event-name" />
        </div>
      )}

      {/* Payment */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {PAYMENT_OPTS.filter(o => o.id !== 'paymongo' || paymongoOn).map(opt => (
          <button key={opt.id} onClick={() => setPaymentMethod(opt.id)} data-testid={`walkin-pay-${opt.id}`}
            style={{ flex: 1, padding: '8px 4px', borderRadius: 8, border: `1.5px solid ${paymentMethod === opt.id ? '#E63946' : '#2A2A2A'}`, background: paymentMethod === opt.id ? '#E6394614' : '#0A0A0A', cursor: 'pointer', textAlign: 'center' }}>
            <p style={{ fontSize: 12, marginBottom: 1 }}>{opt.icon}</p>
            <p style={{ fontSize: 10, fontWeight: 700, color: paymentMethod === opt.id ? '#E63946' : '#555', textTransform: 'uppercase' }}>{opt.label}</p>
          </button>
        ))}
      </div>
      {paymentMethod === 'paymongo' && (
        <p style={{ fontSize: 11, color: '#00E676', marginTop: -8, marginBottom: 12, lineHeight: 1.4 }} data-testid="walkin-paymongo-hint">
          After confirming, a payment link + QR will open automatically so the customer can pay online (Full / 50% / Custom).
        </p>
      )}

      {/* Equipment Rentals */}
      {selectedStart && selectedEnd && (
        <div style={{ marginBottom: 14 }}>
          {equipLoading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0', color: '#555', fontSize: 12 }}>
              <div className="animate-spin" style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #E63946', borderTopColor: 'transparent' }} />
              Checking equipment…
            </div>
          )}
          {!equipLoading && equipmentAvail && equipmentAvail.length > 0 && (
            <div style={{ borderRadius: 10, border: '1px solid #2A2A2A', overflow: 'hidden' }}>
              <div style={{ padding: '8px 12px', background: '#111', borderBottom: '1px solid #2A2A2A' }}>
                <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#737373', fontWeight: 700 }}>Equipment Rentals (Optional)</p>
              </div>
              {equipmentAvail.map(eq => {
                const qty = selectedRentals[eq.equipment_id] || 0;
                const firstAvailWindow = eq.windows.find(w => w.available_qty > 0);
                const maxAvail = firstAvailWindow?.available_qty || 0;
                const isUnavail = eq.min_available === 0;
                const priceLabel = eq.rental_price > 0
                  ? `₱${eq.rental_price.toLocaleString()} / ${eq.rental_price_type === 'per_hour' ? 'hr' : 'session'}`
                  : 'Free';
                return (
                  <div key={eq.equipment_id} data-testid={`walkin-rental-item-${eq.equipment_id}`}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', borderBottom: '1px solid #1A1A1A' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: isUnavail ? '#444' : '#ccc' }}>{eq.name}</p>
                        <span style={{ fontSize: 9, fontWeight: 700, color: eq.rental_price > 0 ? '#E63946' : '#444', background: eq.rental_price > 0 ? '#E6394618' : '#1A1A1A', padding: '1px 5px', borderRadius: 3 }}>{priceLabel}</span>
                      </div>
                      {isUnavail
                        ? <p style={{ fontSize: 10, color: '#FF2D55', marginTop: 1 }}>Unavailable</p>
                        : <p style={{ fontSize: 10, color: '#555', marginTop: 1 }}>{maxAvail} {eq.unit}(s) available</p>
                      }
                    </div>
                    {!isUnavail && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button onClick={() => setSelectedRentals(r => ({ ...r, [eq.equipment_id]: Math.max(0, (r[eq.equipment_id] || 0) - 1) }))}
                          disabled={qty === 0}
                          style={{ width: 26, height: 26, borderRadius: 5, border: '1px solid #2A2A2A', background: '#0A0A0A', color: '#fff', cursor: qty === 0 ? 'not-allowed' : 'pointer', fontSize: 14, opacity: qty === 0 ? 0.3 : 1 }}>
                          −
                        </button>
                        <span style={{ fontSize: 13, fontWeight: 700, color: qty > 0 ? '#E63946' : '#555', minWidth: 16, textAlign: 'center' }}>{qty}</span>
                        <button onClick={() => setSelectedRentals(r => ({ ...r, [eq.equipment_id]: Math.min(maxAvail, (r[eq.equipment_id] || 0) + 1) }))}
                          disabled={qty >= maxAvail}
                          data-testid={`walkin-rental-add-${eq.equipment_id}`}
                          style={{ width: 26, height: 26, borderRadius: 5, border: '1px solid #2A2A2A', background: qty >= maxAvail ? '#0A0A0A' : '#E6394614', color: qty >= maxAvail ? '#444' : '#E63946', cursor: qty >= maxAvail ? 'not-allowed' : 'pointer', fontSize: 14 }}>
                          +
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Rental total subtotal */}
              {Object.values(selectedRentals).some(q => q > 0) && (() => {
                const rentDurationHrs = (toMin(selectedEnd) - toMin(selectedStart)) / 60;
                let rentalTotal = 0;
                const lines = Object.entries(selectedRentals).filter(([,q]) => q > 0).map(([id, q]) => {
                  const eq = equipmentAvail.find(e => e.equipment_id === id);
                  if (!eq) return null;
                  const itemTotal = eq.rental_price > 0
                    ? (eq.rental_price_type === 'per_hour' ? eq.rental_price * q * rentDurationHrs : eq.rental_price * q)
                    : 0;
                  rentalTotal += itemTotal;
                  return { eq, q, itemTotal };
                }).filter(Boolean);
                return (
                  <div style={{ padding: '8px 12px', background: '#E6394608', borderTop: '1px solid #E6394630' }}>
                    {lines.map(({ eq, q, itemTotal }, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <p style={{ fontSize: 11, color: '#888' }}>{q}× {eq.name}{eq.rental_price_type === 'per_hour' ? ` × ${rentDurationHrs}hr` : ''}</p>
                        <p style={{ fontSize: 11, color: itemTotal > 0 ? '#E63946' : '#555', fontWeight: 600 }}>{itemTotal > 0 ? `₱${itemTotal.toLocaleString()}` : 'Free'}</p>
                      </div>
                    ))}
                    {rentalTotal > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, paddingTop: 4, borderTop: '1px solid #E6394630' }}>
                        <p style={{ fontSize: 11, fontWeight: 700, color: '#E63946' }}>Rental Subtotal</p>
                        <p style={{ fontSize: 11, fontWeight: 700, color: '#E63946' }}>₱{rentalTotal.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Notes */}
      <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
        placeholder="Notes (optional)…"
        style={{ ...inputStyle, resize: 'none', marginBottom: 14 }} />

      {/* Voucher */}
      {selectedStart && selectedEnd && amount > 0 && (
        <div style={{ marginBottom: 14 }}>
          <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#737373', marginBottom: 7, fontWeight: 700 }}>Voucher code</p>
          {voucherInfo ? (
            <div style={{ background: '#00E67614', border: '1px solid #00E67655', borderRadius: 8, padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 800, color: '#00E676', fontFamily: 'Oswald, sans-serif' }}>{voucherInfo.code} · {voucherInfo.label}</p>
                <p style={{ fontSize: 11, color: '#8a8a8a', marginTop: 2 }}>
                  {voucherInfo.free_minutes ? `Free +${voucherInfo.free_minutes} min → ends ${fmt12(voucherInfo.extended_end)}` : `−₱${Number(voucherInfo.discount_amount).toLocaleString()} → ₱${Number(voucherInfo.new_amount).toLocaleString()}`}
                </p>
              </div>
              <button type="button" onClick={clearVoucher} data-testid="walkin-voucher-clear" style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', padding: 4 }}><X style={{ width: 16, height: 16 }} /></button>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input data-testid="walkin-voucher-input" value={voucherCode} onChange={e => setVoucherCode(e.target.value.toUpperCase())}
                  placeholder="e.g. PLAY30-A3K9" style={{ ...inputStyle, marginBottom: 0, flex: 1 }} />
                <button type="button" onClick={applyVoucher} disabled={voucherBusy || !voucherCode.trim()} data-testid="walkin-voucher-apply"
                  style={{ background: '#00E676', color: '#0A0A0A', border: 'none', borderRadius: 8, padding: '0 16px', fontWeight: 800, fontFamily: 'Oswald, sans-serif', cursor: 'pointer', opacity: (voucherBusy || !voucherCode.trim()) ? 0.5 : 1 }}>
                  {voucherBusy ? '…' : 'Apply'}
                </button>
              </div>
              {voucherErr && <p data-testid="walkin-voucher-error" style={{ fontSize: 11, color: '#FF2D55', marginTop: 6 }}>{voucherErr}</p>}
            </div>
          )}
        </div>
      )}

      {/* Discount — hidden when a voucher is applied (not combinable) */}
      {!voucherInfo && selectedStart && selectedEnd && amount > 0 && (
        <div style={{ marginBottom: 14 }}>
          <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#737373', marginBottom: 7, fontWeight: 700 }}>
            Discount (optional)
          </p>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {/* % / ₱ toggle */}
            <div style={{ display: 'flex', background: '#0A0A0A', border: '1px solid #2A2A2A', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
              {[['percent', '%'], ['flat', '₱']].map(([t, lbl]) => (
                <button key={t} type="button" onClick={() => { setDiscountType(t); setDiscountValue(''); }}
                  style={{ padding: '8px 14px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'Oswald, sans-serif',
                    background: discountType === t ? '#00E676' : 'transparent',
                    color: discountType === t ? '#0A0A0A' : '#555', transition: 'all 0.15s' }}>
                  {lbl}
                </button>
              ))}
            </div>
            <input
              data-testid="walkin-discount-input"
              type="number" min="0" max={discountType === 'percent' ? 100 : undefined}
              step={discountType === 'percent' ? '1' : '50'}
              value={discountValue}
              onChange={e => setDiscountValue(e.target.value)}
              placeholder={discountType === 'percent' ? 'e.g. 20' : 'e.g. 500'}
              style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
            />
            {discountValue && (
              <button type="button" onClick={() => setDiscountValue('')}
                style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', padding: 4, flexShrink: 0 }}>
                <X style={{ width: 14, height: 14 }} />
              </button>
            )}
          </div>
          {discountValue && (() => {
            const durationHrs = (toMin(selectedEnd) - toMin(selectedStart)) / 60;
            const rentalTotal = Object.entries(selectedRentals).filter(([,q]) => q > 0).reduce((sum, [id, q]) => {
              const eq = (equipmentAvail || []).find(e => e.equipment_id === id);
              if (!eq || !eq.rental_price) return sum;
              return sum + (eq.rental_price_type === 'per_hour' ? eq.rental_price * q * durationHrs : eq.rental_price * q);
            }, 0);
            const subtotal = amount + rentalTotal;
            const dv = parseFloat(discountValue) || 0;
            const saving = discountType === 'percent'
              ? Math.min(dv > 0 ? subtotal * dv / 100 : 0, subtotal)
              : Math.min(dv, subtotal);
            if (saving <= 0) return null;
            return (
              <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ fontSize: 11, color: '#00E676' }}>
                  Saving {discountType === 'percent' ? `${discountValue}%` : `₱${parseFloat(discountValue).toLocaleString()}`}
                </p>
                <p style={{ fontSize: 13, fontWeight: 800, color: '#00E676', fontFamily: 'Oswald, sans-serif' }}>
                  −₱{saving.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                </p>
              </div>
            );
          })()}
        </div>
      )}
    </>
  );

  // ── Shared desk widgets (Find Booking + Open Balances) — rendered in BOTH
  //    the desktop and mobile layouts so cashiers on a phone can collect too. ──
  const findControls = () => (
    <>
      {/* Open balances today — the "who still owes us" pill */}
      <button onClick={() => { setBalancesOpen(v => !v); setFindMode(false); loadOpenBalances(); }}
        data-testid="walkin-open-balances-pill"
        style={{
          background: balancesOpen ? '#E63946' : (openBalances?.length ? '#E6394620' : '#141414'),
          border: `1.5px solid ${openBalances?.length || balancesOpen ? '#E63946' : '#2A2A2A'}`,
          color: balancesOpen ? '#fff' : (openBalances?.length ? '#E63946' : '#737373'),
          borderRadius: 999, padding: '7px 16px', fontSize: 12, fontWeight: 800,
          cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 0.5,
          display: 'flex', alignItems: 'center', gap: 7,
          boxShadow: openBalances?.length && !balancesOpen ? '0 0 14px #E6394630' : 'none',
        }}>
        ₱ Open Balances
        <span style={{ background: balancesOpen ? '#fff' : '#E63946', color: balancesOpen ? '#E63946' : '#fff', borderRadius: 999, padding: '1px 8px', fontSize: 11, fontWeight: 900 }}>
          {openBalances ? openBalances.length : '…'}
        </span>
      </button>
      <button onClick={() => { setFindMode(f => !f); setBalancesOpen(false); }}
        data-testid="walkin-find-booking-btn"
        style={{
          background: findMode ? '#2979FF' : '#2979FF22',
          border: '1.5px solid #2979FF', color: findMode ? '#fff' : '#2979FF',
          borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 800,
          cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 0.5,
        }}>
        {findMode ? '← New Booking' : '🔍 Find Booking'}
      </button>
    </>
  );

  const findPanel = () => !findMode ? null : (
    <div style={{ padding: '16px 24px', background: '#0A0A0A', borderBottom: '1px solid #1A1A1A' }}>
      <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, color: '#2979FF', fontWeight: 700, marginBottom: 10 }}>
        Find Booking & Collect Payment
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input value={searchQ} onChange={e => setSearchQ(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          placeholder="Name, phone, BRN — or scan a QR into this box…"
          data-testid="walkin-search-input"
          style={{ flex: 1, minWidth: 180, background: '#141414', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8, padding: '9px 12px', outline: 'none', fontFamily: 'Manrope, sans-serif', fontSize: 13 }} />
        <input type="date" value={searchDate} onChange={e => setSearchDate(e.target.value)}
          title="Optional — leave empty to search ALL dates"
          style={{ background: '#141414', border: '1px solid #2A2A2A', color: '#737373', borderRadius: 8, padding: '8px 10px', outline: 'none', fontSize: 12 }} />
        <button onClick={handleSearch} disabled={searching}
          data-testid="walkin-search-btn"
          style={{ background: '#2979FF', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 0.5, opacity: searching ? 0.7 : 1 }}>
          {searching ? '…' : 'Search'}
        </button>
      </div>
      <p style={{ fontSize: 11, color: '#555', marginTop: -6, marginBottom: 12 }}>
        Searches EVERYTHING on any date — bookings, open play players, tournament teams.
        Set the date box only to narrow name/phone booking matches. A USB scanner types straight into this box.
      </p>

      {searchResults !== null && (
        <div style={{ maxHeight: 380, overflowY: 'auto' }}>
          {searchResults.length === 0
            && !(searchExtras?.players?.length) && !(searchExtras?.teams?.length) && (
            <p style={{ fontSize: 13, color: '#555', textAlign: 'center', padding: '16px 0' }}>
              Nothing found — searched bookings (all dates), open play players, and tournament teams.
            </p>
          )}
          {searchResults.map(b => {
            const isPaid = bookingIsPaid(b);
            const bal = bookingBalance(b);
            const canMarkPaid = !isPaid && b.status === 'confirmed';
            return (
              <div key={b.id} data-testid={`walkin-search-result-${b.id}`}
                onClick={() => setDetailBooking(b)}
                style={{ background: '#141414', border: `1px solid ${canMarkPaid ? '#E6394630' : '#1E1E1E'}`, borderRadius: 10, padding: '12px 14px', marginBottom: 8, cursor: 'pointer' }}>
                {/* Top row: name + status badges */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
                  <div>
                    <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 900, color: '#fff', textTransform: 'uppercase' }}>{b.customer?.name || '—'}</p>
                    <p style={{ fontSize: 11, color: '#555' }}>{b.court_name} · {fmtDateShort(b.date)} · {fmt12(b.start_time)}–{fmt12(b.end_time)}</p>
                    {b.ref_number && <p style={{ fontSize: 11, color: '#E63946', fontWeight: 700, letterSpacing: 1, fontFamily: 'Oswald, sans-serif', marginTop: 2 }}>{b.ref_number}</p>}
                  </div>
                  {/* Payment status pill */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5,
                      background: isPaid ? '#00E67618' : '#E6394618', color: isPaid ? '#00E676' : '#E63946' }}>
                      {isPaid ? 'PAID' : 'BALANCE DUE'}
                    </span>
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: b.status === 'confirmed' ? '#00E67614' : '#FFB30014', color: b.status === 'confirmed' ? '#00E676' : '#FFB300', textTransform: 'uppercase', fontWeight: 700 }}>
                      {b.status.replace('_',' ')}
                    </span>
                  </div>
                </div>
                {/* Paid / balance split + proof chip */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: '#00E676' }}>Paid ₱{(parseFloat(b.amount_paid) || 0).toLocaleString()}</span>
                  {!isPaid && <span style={{ fontSize: 11, color: '#E63946' }}>· Balance ₱{bal.toLocaleString()}</span>}
                  {b.proof_key && <ProofPreviewButton bookingId={b.id} label="📎 Proof" style={{ padding: '3px 9px', fontSize: 10 }} />}
                </div>
                {/* Bottom row: amount + open-details affordance */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, fontWeight: 900, color: canMarkPaid ? '#E63946' : '#555' }}>₱{(b.amount||0).toLocaleString()}</p>
                  <span data-testid={`walkin-open-detail-${b.id}`}
                    style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: '#2979FF' }}>
                    {canMarkPaid ? 'View & Collect →' : 'View details →'}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Open play players (any session, any date) */}
          {(searchExtras?.players || []).length > 0 && (
            <>
              <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, color: '#8B5CF6', fontWeight: 700, margin: '10px 0 6px' }}>Open Play Players</p>
              {searchExtras.players.map(p => {
                const bal = p.balance_due != null ? p.balance_due
                  : Math.max(0, (p.amount_due || 0) - (p.amount_paid || 0));
                const paid = p.payment_status === 'paid' || p.payment_status === 'free' || bal <= 0;
                return (
                  <div key={p.id} data-testid={`walkin-player-${p.id}`}
                    style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 10, padding: '10px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <div>
                      <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 900, color: '#fff', textTransform: 'uppercase' }}>{p.name}</p>
                      <p style={{ fontSize: 11, color: '#555' }}>{p.session_name} · {p.session_date}{p.phone ? ` · ${p.phone}` : ''}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 5, fontWeight: 800, textTransform: 'uppercase', background: paid ? '#00E67618' : '#E6394618', color: paid ? '#00E676' : '#E63946' }}>
                        {paid ? 'PAID' : `OWES ₱${Number(bal).toLocaleString()}`}
                      </span>
                      <button onClick={() => openInScanner(`/sports/open-play/player/${p.personal_token || p.id}`)}
                        style={{ background: '#8B5CF618', border: '1px solid #8B5CF640', color: '#8B5CF6', borderRadius: 6, padding: '6px 12px', fontSize: 10, fontWeight: 800, cursor: 'pointer', textTransform: 'uppercase' }}>
                        Open
                      </button>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Tournament teams (any event, any date) */}
          {(searchExtras?.teams || []).length > 0 && (
            <>
              <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, color: '#FFB300', fontWeight: 700, margin: '10px 0 6px' }}>Tournament Teams</p>
              {searchExtras.teams.map(tm => {
                const bal = tm.balance_due || 0;
                const paid = tm.payment_status === 'paid' || tm.payment_status === 'free' || bal <= 0;
                return (
                  <div key={tm.id} data-testid={`walkin-team-${tm.id}`}
                    style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 10, padding: '10px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <div>
                      <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 900, color: '#fff', textTransform: 'uppercase' }}>{tm.checked_in_at ? '🟢 ' : ''}{tm.team_name}</p>
                      <p style={{ fontSize: 11, color: '#555' }}>{tm.tournament_name} · {tm.tournament_date}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 5, fontWeight: 800, textTransform: 'uppercase', background: paid ? '#00E67618' : '#E6394618', color: paid ? '#00E676' : '#E63946' }}>
                        {paid ? 'PAID' : `OWES ₱${Number(bal).toLocaleString()}`}
                      </span>
                      <button onClick={() => openInScanner(`/sports/tournament/${tm.tournament_id}/team/${tm.id}`)}
                        style={{ background: '#FFB30018', border: '1px solid #FFB30040', color: '#FFB300', borderRadius: 6, padding: '6px 12px', fontSize: 10, fontWeight: 800, cursor: 'pointer', textTransform: 'uppercase' }}>
                        Open
                      </button>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );

  const balancesPanel = () => !balancesOpen ? null : (
    <div style={{ padding: '16px 24px', background: '#0A0A0A', borderBottom: '1px solid #1A1A1A' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, color: '#E63946', fontWeight: 700 }}>
          Open Balances — Today ({fmtDateShort(ymdTz(facilityTz))})
        </p>
        <p style={{ fontSize: 11, color: '#555' }}>
          Total owed: <b style={{ color: '#E63946' }}>
            ₱{(openBalances || []).reduce((s, b) => s + (parseFloat(b.balance_due) || 0), 0).toLocaleString()}
          </b>
        </p>
      </div>
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {(openBalances || []).length === 0 && (
          <p style={{ fontSize: 13, color: '#00E676', textAlign: 'center', padding: '14px 0' }}>
            ✓ No open balances today — everyone playing today is settled.
          </p>
        )}
        {(openBalances || []).map(b => (
          <div key={b.id} data-testid={`walkin-balance-${b.id}`}
            onClick={() => setDetailBooking(b)}
            style={{ background: '#141414', border: '1px solid #E6394626', borderRadius: 10, padding: '10px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}>
            <div style={{ flexShrink: 0, textAlign: 'center', minWidth: 86 }}>
              <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 900, color: '#fff' }}>{fmt12(b.start_time)}</p>
              <p style={{ fontSize: 10, color: '#555' }}>– {fmt12(b.end_time)}</p>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 900, color: '#fff', textTransform: 'uppercase' }}>{b.customer?.name || '—'}</p>
              <p style={{ fontSize: 11, color: '#555' }}>{b.court_name}{b.ref_number ? ` · ${b.ref_number}` : ''}{b.checked_in_at ? ' · 🟢 checked in' : ''}</p>
              {b.proof_key && <div style={{ marginTop: 4 }}><ProofPreviewButton bookingId={b.id} label="📎 Proof" style={{ padding: '3px 9px', fontSize: 10 }} /></div>}
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 900, color: '#E63946' }}>
                ₱{(parseFloat(b.balance_due) || 0).toLocaleString()}
              </p>
              <p style={{ fontSize: 10, color: '#2979FF', fontWeight: 800, textTransform: 'uppercase' }}>Collect →</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const detailModal = () => !detailBooking ? null : (
    <WalkinBookingModal
      key={detailBooking.id}
      booking={detailBooking}
      onClose={() => setDetailBooking(null)}
      onPaid={handleModalPaid}
    />
  );

  // ── DESKTOP LAYOUT ────────────────────────────────────────────
  if (isDesktop) return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
      <SportsNavBar />

      {/* POS Header bar */}
      <div style={{ background: '#0D0D0D', borderBottom: '1px solid #1A1A1A', padding: '10px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 2, color: '#E63946' }}>Walk-In</p>
          <span style={{ color: '#2A2A2A', fontSize: 20 }}>|</span>
          <p style={{ fontSize: 13, color: '#555' }}>Court Cashier</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {findControls()}
          <p style={{ fontSize: 12, color: '#555', fontFamily: 'monospace', display: isDesktop ? 'block' : 'none' }}>
            {new Date().toLocaleDateString('en-PH', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* ── Find Booking + Open Balances panels + detail modal (shared) ── */}
      {findPanel()}
      {balancesPanel()}
      {detailModal()}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 420px', height: `calc(100vh - ${findMode ? '220px' : '108px'})`, overflow: 'hidden' }}>

        {/* ── LEFT: Court + Date + Slots ── */}
        <div style={{ padding: '16px 20px', overflowY: 'auto', borderRight: '1px solid #1A1A1A', display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Court tabs */}
          <div>
            <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, color: '#333', marginBottom: 8, fontWeight: 700 }}>Court</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {courts.map(c => (
                <button key={c.id} onClick={() => setSelectedCourt(c)}
                  data-testid={`walkin-court-${c.id}`}
                  style={{
                    padding: '8px 18px', borderRadius: 10,
                    border: `1.5px solid ${selectedCourt?.id === c.id ? '#E63946' : '#2A2A2A'}`,
                    background: selectedCourt?.id === c.id ? '#E6394614' : '#141414',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: selectedCourt?.id === c.id ? '#E63946' : '#fff', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 0.5 }}>{c.name}</p>
                  <p style={{ fontSize: 10, color: '#444', textTransform: 'capitalize' }}>{c.sport} · {fmt12(c.open_time)}–{fmt12(c.close_time)}</p>
                  {(() => {
                    const g = courtGlance[c.id];
                    if (!g) return null;
                    if (!g.open) return <p style={{ fontSize: 9.5, color: '#555', fontStyle: 'italic', marginTop: 3 }}>Closed this day</p>;
                    const ivs = (g.ivs || []).filter(iv => !slotIsPastWalkin(iv)).map(iv => {
                      if (date !== todayInTz || toMin(iv.start) > facilityNowMin) return iv;
                      const nb = Math.ceil((facilityNowMin + 1) / 30) * 30;
                      if (nb >= toMin(iv.end)) return null;
                      return { ...iv, start: `${String(Math.floor(nb / 60)).padStart(2, '0')}:${String(nb % 60).padStart(2, '0')}` };
                    }).filter(Boolean);
                    if (!ivs.length) return <p style={{ fontSize: 9.5, color: '#E63946', fontWeight: 700, marginTop: 3 }}>Fully booked</p>;
                    return (
                      <p style={{ fontSize: 9.5, color: '#22C55E', fontWeight: 600, marginTop: 3 }} data-testid={`walkin-glance-${c.id}`}>
                        Free: {ivs.slice(0, 2).map(iv => `${fmt12(iv.start)}–${fmt12(iv.end)}`).join(' · ')}{ivs.length > 2 ? ` +${ivs.length - 2}` : ''}
                      </p>
                    );
                  })()}
                </button>
              ))}
            </div>
          </div>

          {/* Date nav */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, color: '#333', fontWeight: 700, whiteSpace: 'nowrap' }}>Date</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#141414', borderRadius: 10, padding: '6px 10px', border: '1px solid #1E1E1E' }}>
              <button onClick={() => changeDate(-1)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', padding: 2, display: 'flex' }}>
                <ChevronLeft style={{ width: 16, height: 16 }} />
              </button>
              <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 14, fontWeight: 700, color: '#fff', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
                {fmtDateFull(date)}
                {date === todayInTz && <span style={{ color: '#E63946', fontSize: 11, marginLeft: 8 }}>TODAY</span>}
              </p>
              <button onClick={() => changeDate(1)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', padding: 2, display: 'flex' }}>
                <ChevronRight style={{ width: 16, height: 16 }} />
              </button>
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid #1A1A1A' }} />

          {/* Slot grid — the main action area */}
          {slotGrid({ compact: false })}
        </div>

        {/* ── RIGHT: Sticky order ticket ── */}
        <div style={{ background: '#0D0D0D', display: 'flex', flexDirection: 'column', borderLeft: '1px solid #1A1A1A', overflow: 'hidden' }}>

          {/* Ticket header */}
          <div style={{ padding: '14px 18px', borderBottom: '1px solid #1A1A1A', flexShrink: 0 }}>
            <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, color: '#333', fontWeight: 700, marginBottom: 4 }}>Booking Ticket</p>
            {selectedStart && selectedEnd ? (
              <div>
                <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 20, fontWeight: 900, color: '#fff' }}>
                  {fmt12(selectedStart)} – {fmt12(selectedEnd)}
                </p>
                <p style={{ fontSize: 12, color: '#555', marginTop: 2 }}>
                  {selectedCourt?.name} · {fmtDateShort(date)}
                </p>
                {(date < todayInTz || (date === todayInTz && toMin(selectedStart) < facilityNowMin)) && (
                  <p data-testid="walkin-past-notice" style={{ fontSize: 11, color: '#F59E0B', marginTop: 6, fontWeight: 700 }}>
                    ⏱ This time has already passed — recording a past walk-in
                  </p>
                )}
              </div>
            ) : (
              <p style={{ fontSize: 13, color: '#333', fontStyle: 'italic' }}>No time selected yet</p>
            )}
          </div>

          {/* Form area */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
            {bookingForm()}
          </div>

          {/* Confirm — always visible at bottom */}
          <div style={{ padding: '12px 18px', borderTop: '1px solid #1A1A1A', flexShrink: 0 }}>
            {selectedStart && selectedEnd && amount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div>
                  <p style={{ fontSize: 12, color: '#555' }}>Total</p>
                  {(() => {
                    const durationHrs = (toMin(selectedEnd) - toMin(selectedStart)) / 60;
                    const rentalTotal = Object.entries(selectedRentals).filter(([,q]) => q > 0).reduce((sum, [id, q]) => {
                      const eq = (equipmentAvail || []).find(e => e.equipment_id === id);
                      if (!eq || !eq.rental_price) return sum;
                      return sum + (eq.rental_price_type === 'per_hour' ? eq.rental_price * q * durationHrs : eq.rental_price * q);
                    }, 0);
                    const subtotal = amount + rentalTotal;
                    const dv = parseFloat(discountValue) || 0;
                    const saving = discountValue && dv > 0
                      ? (discountType === 'percent' ? Math.min(subtotal * dv / 100, subtotal) : Math.min(dv, subtotal))
                      : 0;
                    const finalTotal = subtotal - saving;
                    return (
                      <>
                        {rentalTotal > 0 && <p style={{ fontSize: 10, color: '#555' }}>Court ₱{amount.toLocaleString()} + Rental ₱{rentalTotal.toLocaleString()}</p>}
                        {saving > 0 && (
                          <p style={{ fontSize: 11, color: '#00E676' }}>
                            − Discount ₱{saving.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                          </p>
                        )}
                        <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, fontWeight: 900, color: saving > 0 ? '#00E676' : '#E63946', lineHeight: 1.1 }}>₱{finalTotal.toLocaleString()}</p>
                        {saving > 0 && <p style={{ fontSize: 10, color: '#555', textDecoration: 'line-through' }}>₱{subtotal.toLocaleString()}</p>}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
            <button onClick={handleBook} disabled={saving || !selectedStart}
              data-testid="sports-walkin-confirm-btn"
              style={{
                width: '100%', padding: '14px', borderRadius: 12,
                background: saving || !selectedStart ? '#1A1A1A' : '#E63946',
                color: saving || !selectedStart ? '#333' : '#fff',
                border: 'none', cursor: saving || !selectedStart ? 'not-allowed' : 'pointer',
                fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 900,
                textTransform: 'uppercase', letterSpacing: 1,
                boxShadow: !saving && selectedStart ? '0 0 24px rgba(230,57,70,0.35)' : 'none',
                transition: 'all 0.2s',
              }}>
              {saving ? 'Processing…' : selectedStart ? `Confirm ${bookingType === 'competition' ? 'Event' : 'Walk-In'}` : 'Select a Time Slot'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ── MOBILE LAYOUT ─────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
      <SportsNavBar />
      <main className="max-w-xl mx-auto px-4 py-8">
        <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#737373' }}>Staff</p>
        <h1 className="text-3xl uppercase tracking-tight mb-4" style={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700 }}>Walk-In Booking</h1>

        {/* Find Booking / Open Balances — now reachable on mobile, not just desktop */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {findControls()}
        </div>
        {(findMode || balancesOpen) && (
          <div style={{ marginBottom: 16, borderRadius: 12, overflow: 'hidden', border: '1px solid #1A1A1A' }}>
            {findPanel()}
            {balancesPanel()}
          </div>
        )}
        {detailModal()}

        <div className="space-y-4">
          {/* Court selector */}
          <div className="rounded-xl p-4" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
            <p className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: '#737373' }}>Court</p>
            <div className="flex flex-wrap gap-2">
              {courts.map(c => (
                <button key={c.id} onClick={() => setSelectedCourt(c)}
                  className="flex-1 text-left rounded-xl px-4 py-3 transition-all"
                  style={{ border: `1.5px solid ${selectedCourt?.id === c.id ? '#E63946' : '#2A2A2A'}`, background: selectedCourt?.id === c.id ? '#E6394614' : '#0A0A0A', minWidth: 120 }}>
                  <p className="text-sm font-bold" style={{ color: selectedCourt?.id === c.id ? '#E63946' : '#fff', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}>{c.name}</p>
                  <p className="text-xs capitalize" style={{ color: '#555' }}>{c.sport}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Date nav */}
          <div className="rounded-xl p-4" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => changeDate(-1)} className="p-2 rounded-lg" style={{ background: '#1A1A1A', color: '#A3A3A3' }}>
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="text-center">
                <p className="text-sm font-bold" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>{fmtDateFull(date)}</p>
                {date === todayInTz && <p className="text-xs" style={{ color: '#E63946' }}>Today</p>}
              </div>
              <button onClick={() => changeDate(1)} className="p-2 rounded-lg" style={{ background: '#1A1A1A', color: '#A3A3A3' }}>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            {slotGrid({ compact: true })}
          </div>

          {/* Booking form */}
          <div className="rounded-xl p-4" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
            <p className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: '#737373' }}>Booking Details</p>
            {bookingForm()}
          </div>

          {/* Confirm */}
          <button onClick={handleBook} disabled={saving || !selectedStart}
            data-testid="sports-walkin-confirm-btn"
            className="w-full py-4 rounded-xl font-bold text-sm uppercase tracking-wide"
            style={{ background: saving || !selectedStart ? '#1A1A1A' : '#E63946', color: saving || !selectedStart ? '#555' : '#fff', border: 'none', cursor: saving || !selectedStart ? 'not-allowed' : 'pointer', fontFamily: 'Oswald, sans-serif', fontSize: 15, letterSpacing: 1 }}>
            {saving ? 'Processing…' : selectedStart ? `Confirm Walk-In${amount > 0 ? ` — ₱${amount.toLocaleString()}` : ''}` : 'Select a Time Slot First'}
          </button>
        </div>
      </main>
    </div>
  );
}
